"use client";

import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { useRouter } from "next/navigation";
import { isValidEmail, suggestEmailDomain, EMAIL_ERROR_MESSAGE } from "@aluume/shared/lib/email";
import { captureAttribution, trackClientEvent } from "@aluume/shared/lib/tracking/client";
import { CountrySelect } from "@/components/checkout/CountrySelect";
import { AddressFields } from "@/components/checkout/AddressFields";
import { COUNTRIES } from "@aluume/shared/lib/countries";
import { combinePhone } from "@aluume/shared/lib/phone";
import { Spinner } from "@aluume/shared/components/ui/Spinner";
import { nzd } from "@aluume/shared/lib/format";
import { VerifonePaymentFrame } from "@/components/checkout/VerifonePaymentFrame";
import { AcceptedCards } from "@/app/checkout/AcceptedCards";
import { PaymentInfoNote } from "@/components/checkout/PaymentInfoNote";
import { CONFIG } from "./content";

// The real "Reserve your place" checkout for /100 — the founder-membership form
// card. Takes the actual $3,000 NZD payment, mirroring the proven gift-voucher
// flow exactly: collect purchaser + billing details → POST /api/membership/
// checkout-init → mount the shared Verifone card iframe → poll /api/membership/
// status until it reports "paid", then show the founder confirmation.
//
// The membership BACKEND (endpoints + table) ships on a sibling card. Until it
// merges, every call degrades gracefully so the page still renders + reviews on
// staging: a failed / 404 checkout-init shows "Reservations open shortly" rather
// than a broken form (see A3 in the plan).

const labelCls = "text-[11px] uppercase tracking-[1.6px] text-ink-soft";
const inputCls = "w-full border-b border-ink/30 bg-transparent py-2 text-[15px] text-ink outline-none focus:border-ink";

type LivePay = { iframeUrl: string; membershipId: string; amount: number };

function Field({ label, value, onChange, type = "text", placeholder, autoComplete, className, onBlur, error, children, inputRef }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; autoComplete?: string; className?: string; onBlur?: () => void; error?: string; children?: ReactNode; inputRef?: Ref<HTMLInputElement>;
}) {
  const fieldId = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const errorId = `${fieldId}-error`;
  return (
    <label htmlFor={fieldId} className={`flex flex-col gap-1.5${className ? ` ${className}` : ""}`}>
      <span className={labelCls}>{label}</span>
      <input
        id={fieldId}
        ref={inputRef}
        type={type}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={`${inputCls}${error ? " border-corten focus:border-corten" : ""}`}
      />
      {error && <span id={errorId} className="text-[12px] text-corten">{error}</span>}
      {children}
    </label>
  );
}

export function MembershipCheckout({ remaining }: { remaining: number | null }) {
  const router = useRouter();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [dialIso, setDialIso] = useState("NZ");
  const [phone, setPhone] = useState("");
  const [addr, setAddr] = useState({ line1: "", city: "", postcode: "", countryIso: "NZ" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [livePay, setLivePay] = useState<LivePay | null>(null);
  // Set the instant the poll sees "paid", to swap the iframe for a brief spinner while
  // we route to the dedicated full-page confirmation (no inline confirmation card).
  const [finishing, setFinishing] = useState(false);

  // Warm-up ping (card 987bd9db): the slowest OUR-side piece of Reserve → card
  // form is a cold checkout-init lambda (~1.5–2s, and /100 is low-traffic so a
  // real customer usually lands cold). Fire a read-only GET at the SAME route —
  // each Vercel route is its own lambda, so pinging anything else warms nothing —
  // while the customer is still on the page: after load on an idle callback, or
  // on their first interaction with the form, whichever comes first. Once per
  // page view; 204, no writes, no Verifone session.
  const warmedRef = useRef(false);
  useEffect(() => {
    const warm = () => {
      if (warmedRef.current) return;
      warmedRef.current = true;
      void fetch("/api/membership/checkout-init?warm=1", { cache: "no-store" }).catch(() => {});
    };
    // No load/readyState gating — the component mounts with the page, so a plain
    // timer from mount is the reliable floor (an earlier version gated on the
    // load event, which can have already fired by hydration time, leaving the
    // warm unarmed). requestIdleCallback fires it sooner when the thread is free;
    // first focus of the reserve form fires it immediately for a #reserve deep
    // link. warmedRef makes all three idempotent — the ping goes once per view.
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    ric?.(warm, { timeout: 2500 });
    const timer = setTimeout(warm, 2500);
    const el = document.getElementById("reserve");
    el?.addEventListener("focusin", warm, { once: true });
    return () => {
      clearTimeout(timer);
      el?.removeEventListener("focusin", warm);
    };
  }, []);

  const soldOut = remaining === 0;

  // Billing address is required (3-D Secure mandates address1 + city + country).
  const billingComplete = !!(addr.line1.trim() && addr.city.trim() && addr.countryIso.trim());
  // Email is validated on click (enabled-with-error) rather than gating the button,
  // so a mistyped address surfaces a clear message instead of a dead control.
  const canSubmit = !busy && !!first.trim() && acceptedTerms && billingComplete;

  async function startPayment() {
    if (!canSubmit) return;
    // Surface a clear inline email error (+ typo hint) and stop before payment —
    // the server re-validates as the authority.
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR_MESSAGE);
      setEmailSuggestion(suggestEmailDomain(email));
      emailRef.current?.focus();
      return;
    }
    setBusy(true);
    setError("");
    setUnavailable(false);
    try {
      const body = JSON.stringify({
        purchaser_name: `${first} ${last}`.trim(),
        purchaser_email: email.trim(),
        purchaser_phone: phone ? combinePhone(COUNTRIES.find((c) => c.iso === dialIso)?.dial ?? "", phone) : null,
        marketing_consent: consent,
        // Billing address → Verifone customer.billing for 3-D Secure.
        billing_address1: addr.line1.trim() || null,
        billing_city: addr.city.trim() || null,
        billing_country: addr.countryIso.trim() || null,
        // The Reserve button already gates on acceptedTerms (canSubmit); forward the signal so the
        // server can ENFORCE it and record acceptance against the $3,000 membership.
        terms_accepted: acceptedTerms,
      });
      const post = () => fetch("/api/membership/checkout-init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      let res = await post();
      // 202 = a concurrent submit (e.g. a second tab) is opening the payment session; the
      // dedup guard means only one payable session ever exists. Retry once, briefly, to pick
      // up that shared session rather than showing an error.
      if (res.status === 202) {
        await new Promise((r) => setTimeout(r, 700));
        res = await post();
      }
      // Pre-merge / not-yet-live backend → degrade to "opening shortly" rather
      // than a scary error, so the page is still reviewable on staging.
      if (res.status === 404) { setUnavailable(true); return; }
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.iframeUrl || !j.membership_id) {
        // Per-IP throttle tripped (griefing guard) — surface a calm "wait a moment"
        // message rather than the generic "Reservations open shortly" fall-through.
        if (res.status === 429 || j.error === "rate_limited") { setError("Too many attempts. Please wait a few minutes and try again."); return; }
        if (j.error === "membership_discontinued") { setError("Founder memberships have been discontinued, so this can no longer be purchased."); return; }
        if (j.error === "sold_out") { setError("All one hundred founder memberships have now been reserved."); return; }
        // Membership is personal & non-transferable — one per email. Read as a calm
        // "you already hold one" message, not the generic "opening shortly".
        if (j.error === "already_member") { setError("This email already holds a founder membership. Check your inbox for your confirmation, or contact us if you need a hand."); return; }
        // Still initialising after the retry — another submit is finishing; ask for one more tap.
        if (res.status === 202 || j.error === "initializing") { setError("Still opening your payment. Please tap Reserve again in a moment."); return; }
        if (j.error === "payments_unavailable") { setError("Card payments are temporarily unavailable. Please try again shortly."); return; }
        // Server refused because acceptance wasn't recorded — shouldn't be reachable from the UI
        // (the button gates on the checkbox), but handled so it reads as a terms prompt, not a
        // generic "opening shortly".
        if (j.error === "terms_not_accepted") { setError("Please tick the box to accept the Thermae100 Membership Terms before reserving."); return; }
        // A server email rejection must read as an email problem, not "opening shortly".
        if (j.error === "invalid_email") { setEmailError(j.message ?? EMAIL_ERROR_MESSAGE); emailRef.current?.focus(); return; }
        setUnavailable(true);
        return;
      }
      const amount = Number(j.amount ?? CONFIG.priceMembership);
      setLivePay({ iframeUrl: j.iframeUrl, membershipId: j.membership_id, amount });
      // Funnel: begin_checkout at the moment the reserve form is submitted and a real
      // payment session opens (GTM → GA4 begin_checkout + Meta InitiateCheckout). Refresh
      // attribution first so the purchase_event_id survives to the confirmation page.
      captureAttribution();
      trackClientEvent("begin_checkout", {
        ecommerce: {
          currency: "NZD",
          value: amount,
          items: [{ item_id: "founder_100", item_name: "100 by Aluume — Founder Membership", price: amount, quantity: 1 }],
        },
      });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  // Poll for the payment outcome (webhook-independent — the status route asks
  // Verifone directly). On success the membership is activated + emailed server-side.
  useEffect(() => {
    if (!livePay) return;
    let active = true;
    let attempts = 0;
    const t = setInterval(async () => {
      if (!active) return;
      attempts += 1;
      if (attempts >= 150) { // ~5 min
        clearInterval(t);
        setError("We couldn't confirm your payment automatically. If you were charged, contact us and we'll sort it out.");
        return;
      }
      try {
        // The status endpoint is email-bound (card 6ec04d8f, issue 5) — send the purchaser email
        // alongside the id so the poll can read the outcome; the id alone no longer authorises it.
        const statusQuery = new URLSearchParams({ id: livePay.membershipId, e: email.trim() });
        const res = await fetch(`/api/membership/status?${statusQuery}`);
        const j = await res.json();
        if (j.status === "paid") {
          clearInterval(t);
          active = false;
          setFinishing(true);
          // Land the customer on the dedicated full-page confirmation, dropping the
          // reserve-steps + checkout chrome. It re-reads status for the authoritative
          // founder number + amount and fires the `purchase` funnel event.
          const params = new URLSearchParams({ id: livePay.membershipId, e: email.trim() });
          router.push(`/100/confirmed?${params}`);
        }
      } catch {}
    }, 2000);
    return () => { active = false; clearInterval(t); };
  }, [livePay, email, router]);

  // ── Finishing (paid → routing to the full-page confirmation) ─────────────────
  if (finishing) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 bg-cream p-[clamp(28px,4vw,44px)] text-center text-ink">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink/20 border-t-ink" />
        <p className="text-[14px] font-light text-ink-soft">Confirming your membership…</p>
      </div>
    );
  }

  // ── Sold out ─────────────────────────────────────────────────────────────────
  if (soldOut) {
    return (
      <div className="bg-cream p-[clamp(28px,4vw,44px)] text-ink">
        <div className="mb-5 border-b border-rule pb-4 text-[12px] tracking-[0.06em] text-mocha">
          <span>Founder memberships</span>
        </div>
        <h3 style={{ fontFamily: "var(--font-display)" }} className="mb-3 text-[24px] font-light text-ink">Fully subscribed.</h3>
        <p className="text-[14px] font-light leading-relaxed text-ink-soft">
          All one hundred founder memberships have been reserved. Leave your details with us at{" "}
          <a href={`mailto:${CONFIG.contactEmail}`} className="text-corten underline">{CONFIG.contactEmail}</a>{" "}
          and we&apos;ll let you know if one becomes available.
        </p>
      </div>
    );
  }

  // ── Payment (Verifone iframe) ────────────────────────────────────────────────
  if (livePay) {
    return (
      <div className="bg-cream p-[clamp(28px,4vw,44px)] text-ink">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <p className="text-[15px] text-ink">{`${first} ${last}`.trim()}</p>
            <p className="text-[13px] font-light text-ink-soft">{email}</p>
          </div>
          <button onClick={() => { setLivePay(null); setError(""); }} className="ui-label text-ink-soft hover:text-ink">Edit</button>
        </div>
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <h3 style={{ fontFamily: "var(--font-display)" }} className="text-[24px] font-light text-ink">Payment</h3>
            <span className="text-[15px] text-ink">NZD {nzd(livePay.amount)}</span>
          </div>
          <AcceptedCards className="-mt-2" />
          <VerifonePaymentFrame iframeUrl={livePay.iframeUrl} />
          <PaymentInfoNote />
          {error && <p className="text-[12px] text-corten">{error}</p>}
        </div>
      </div>
    );
  }

  // ── Details + billing form ───────────────────────────────────────────────────
  return (
    <div className="bg-cream p-[clamp(28px,4vw,44px)] text-ink">
      <div className="mb-6 border-b border-rule pb-4 text-[12px] tracking-[0.06em] text-mocha">
        <span>Founder memberships</span>
      </div>

      {unavailable ? (
        <div className="py-6 text-center">
          <h3 style={{ fontFamily: "var(--font-display)" }} className="mb-3 text-[24px] font-light text-ink">Reservations open shortly.</h3>
          <p className="mx-auto max-w-[40ch] text-[14px] font-light leading-relaxed text-ink-soft">
            Founder checkout is almost ready. Leave your details with us at{" "}
            <a href={`mailto:${CONFIG.contactEmail}`} className="text-corten underline">{CONFIG.contactEmail}</a>{" "}
            and we&apos;ll make sure you&apos;re first to know the moment places can be reserved.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <Field label="First name" value={first} onChange={setFirst} placeholder="Sophie" autoComplete="given-name" />
            <Field label="Last name" value={last} onChange={setLast} placeholder="Chamberlain" autoComplete="family-name" />
            <Field
              label="Email"
              type="email"
              value={email}
              inputRef={emailRef}
              onChange={(v) => { setEmail(v); if (emailError) setEmailError(""); if (emailSuggestion) setEmailSuggestion(null); }}
              onBlur={() => { const v = email.trim(); setEmailError(v && !isValidEmail(v) ? EMAIL_ERROR_MESSAGE : ""); setEmailSuggestion(suggestEmailDomain(v)); }}
              error={emailError}
              placeholder="sophie@example.com"
              autoComplete="email"
              className="col-span-2"
            >
              {emailSuggestion && !emailError && (
                <span className="text-[12px] font-light text-ink-soft">
                  Did you mean{" "}
                  <button type="button" onClick={() => { setEmail(emailSuggestion); setEmailSuggestion(null); setEmailError(""); }} className="text-corten underline">{emailSuggestion}</button>?
                </span>
              )}
            </Field>
            <div className="col-span-2 flex flex-col gap-1.5">
              <span className={labelCls}>Phone</span>
              <div className="flex items-end gap-2">
                <div className="w-fit shrink-0"><CountrySelect value={dialIso} mode="dial" autoComplete="tel-country-code" name="tel-country-code" onChange={(c) => setDialIso(c.iso)} /></div>
                <input type="tel" name="phone" autoComplete="tel-national" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="021 123 4567" className={inputCls} />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <span className={labelCls}>Billing address</span>
            <AddressFields value={addr} onChange={setAddr} required />
          </div>

          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1" />
              <span className="text-[13px] font-light text-ink-soft">
                I&apos;ve read and accept the{" "}
                <a href={CONFIG.termsUrl} target="_blank" rel="noopener noreferrer" className="text-corten underline">Thermae100 Membership Terms</a>, including the 14-day cooling-off refund.
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
              <span className="text-[13px] font-light text-ink-soft">I&apos;d like to receive updates from Aluume.</span>
            </label>
          </div>

          <button
            onClick={startPayment}
            disabled={!canSubmit}
            className="flex h-[54px] w-full items-center justify-center rounded-pill bg-ink text-[13px] uppercase tracking-[2px] text-ink-on-dark transition-colors hover:bg-accent disabled:opacity-40"
          >
            {busy && <Spinner size={16} className="mr-2.5 text-ink-on-dark" />}
            {busy ? "Starting…" : `Reserve · NZD ${nzd(CONFIG.priceMembership)}`}
          </button>
          {!billingComplete && (first || email) && (
            <p className="text-[12px] font-light text-ink-soft">Please add your billing address to continue.</p>
          )}
          {error && <p className="text-[12px] text-corten">{error}</p>}
          <p className="text-center text-[12px] font-light text-ink-soft">
            $3,000 NZD, inc. GST, taken once. Fully refundable for 14 days.
          </p>
        </div>
      )}
    </div>
  );
}
